import type { StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import { FormField } from '@/components/MultiStepForm/FormField/FormField'
import SelectRadix from '@/components/ui/SelectRadix'
import { useLanguagesQuery } from '@/services/staticValues/queries'
import { Switch } from 'radix-ui'
import { DatePickerField } from './DatePickerField'
import styles from './GeneralInfoStep.module.css'
import { formatDateForBackend, parseDateFromBackend } from './dateUtils'
import { useDateConstraints } from './useDateConstraints'

export const GeneralInfoStep = ({ data, updateData, errors }: StepProps) => {
  const { data: languages } = useLanguagesQuery()
  const autoStartEnabled = data.autoStartEnabled ?? false
  const startDate = parseDateFromBackend(data.startDate)
  const endDate = parseDateFromBackend(data.endDate)

  const { tomorrow, maxStartDate, maxEndDate } = useDateConstraints()

  const minEndDate = startDate
    ? (() => {
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        return nextDay
      })()
    : tomorrow

  const handleStartDateChange = (date: Date | undefined) => {
    updateData({ startDate: formatDateForBackend(date) })
  }

  const handleEndDateChange = (date: Date | undefined) => {
    updateData({ endDate: formatDateForBackend(date) })
  }

  return (
    <div className={styles.Container}>
      <FormField label="Language" error={errors?.language}>
        <SelectRadix
          options={languages?.items ? languages?.items : []}
          value={data.language || ''}
          onValueChange={(value) => updateData({ language: value })}
          arialabel="Select Language"
          placeholder="Select a language"
        />
      </FormField>

      <div className={styles.Row}>
        <FormField label="Gender" error={errors?.gender}>
          <SelectRadix
            options={['Any gender', 'Female', 'Male']}
            value={data.gender || ''}
            onValueChange={(value) => updateData({ gender: value })}
            arialabel="Select Gender"
            placeholder="Select a gender"
          />
        </FormField>

        <FormField label="Age" error={errors?.age}>
          <SelectRadix
            options={['Any age', '18-24', '25-34', '35-44', '45+']}
            value={data.age || ''}
            onValueChange={(value) => updateData({ age: value })}
            arialabel="Select Age"
            placeholder="Select an age"
          />
        </FormField>
      </div>

      <div className={styles.SwitchAndInput}>
        <Switch.Root
          className={styles.Root}
          id="instagram"
          checked={autoStartEnabled}
          onCheckedChange={(checked) =>
            updateData({
              autoStartEnabled: checked,
              startDate: checked ? data.startDate : null,
              endDate: checked ? data.endDate : null,
            })
          }
        >
          <Switch.Thumb className={styles.Thumb} />
        </Switch.Root>
        <p className={styles.switcherLabel}>Set a delayed start</p>
      </div>

      <div className={styles.Row}>
        <DatePickerField
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          disabled={!autoStartEnabled}
          error={errors?.startDate}
          minDate={tomorrow}
          maxDate={maxStartDate}
          placeholder={autoStartEnabled ? 'Select start date' : 'Not allowed'}
        />

        <DatePickerField
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          disabled={!autoStartEnabled}
          error={errors?.endDate}
          minDate={minEndDate}
          maxDate={maxEndDate}
          placeholder={autoStartEnabled ? 'Select end date' : 'Not allowed'}
        />
      </div>
    </div>
  )
}
